
import { DataScienceCourse } from './types';

export const DATASCIENCE_FUNDAMENTALS_COURSE: DataScienceCourse = {
  id: 'datascience-fundamentals',
  title: 'Data Science Fundamentals',
  description: 'Master the essential concepts and tools of data science including statistics, Python programming, and data analysis.',
  level: 'fundamentals',
  totalDuration: '20-25 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Basic mathematical concepts (algebra, statistics)',
    'Programming fundamentals',
    'Familiarity with data concepts'
  ],
  learningOutcomes: [
    'Understand core data science concepts and methodology',
    'Apply statistical analysis to real-world datasets',
    'Use Python libraries for data manipulation and analysis',
    'Create meaningful data visualizations',
    'Implement basic machine learning algorithms'
  ],
  modules: [
    {
      id: 'datascience-intro',
      title: 'Introduction to Data Science',
      description: 'Explore the fundamentals of data science and its applications across industries.',
      content: `
# Introduction to Data Science

## What is Data Science?

Data Science is an interdisciplinary field that uses scientific methods, processes, algorithms, and systems to extract knowledge and insights from structured and unstructured data.

## Key Components of Data Science

### Statistics and Mathematics
- Descriptive and inferential statistics
- Probability theory
- Linear algebra and calculus
- Statistical modeling

### Programming and Technology
- Python/R programming languages
- SQL for database management
- Big data technologies
- Cloud computing platforms

### Domain Expertise
- Understanding business problems
- Industry-specific knowledge
- Problem-solving skills
- Communication abilities

## The Data Science Process

### 1. Problem Definition
- Identifying business questions
- Defining success metrics
- Understanding stakeholder needs

### 2. Data Collection
- Gathering relevant data sources
- Data acquisition methods
- Ensuring data quality and completeness

### 3. Data Exploration and Cleaning
- Exploratory data analysis (EDA)
- Data preprocessing
- Handling missing values and outliers

### 4. Modeling and Analysis
- Selecting appropriate algorithms
- Feature engineering
- Model training and validation

### 5. Communication and Deployment
- Data visualization
- Presenting findings to stakeholders
- Model deployment and monitoring

## Applications of Data Science

### Business Intelligence
- Customer segmentation
- Predictive analytics
- Marketing optimization
- Financial forecasting

### Healthcare
- Drug discovery
- Medical image analysis
- Personalized medicine
- Epidemiological studies

### Technology
- Recommendation systems
- Natural language processing
- Computer vision
- Fraud detection

## Tools and Technologies

### Programming Languages
- **Python**: Versatile, extensive libraries
- **R**: Statistical computing and graphics
- **SQL**: Database querying
- **Scala**: Big data processing

### Libraries and Frameworks
- **Pandas**: Data manipulation
- **NumPy**: Numerical computing
- **Matplotlib/Seaborn**: Data visualization
- **Scikit-learn**: Machine learning
      `,
      duration: '60 minutes',
      difficulty: 'beginner',
      order: 1,
      estimatedTime: '60 minutes',
      objectives: [
        'Define data science and its key components',
        'Understand the data science process and methodology',
        'Identify applications of data science across industries',
        'Recognize essential tools and technologies in data science'
      ],
      quiz: {
        questions: [
          {
            id: 'datascience-intro-q1',
            question: 'Which of the following best describes data science?',
            options: [
              'Only statistical analysis of data',
              'An interdisciplinary field extracting knowledge from data',
              'Just programming and software development',
              'Only machine learning algorithms'
            ],
            correctAnswer: 1,
            difficulty: 'easy',
            explanation: 'Data science is an interdisciplinary field that combines statistics, programming, domain expertise, and analytical methods to extract insights from data.',
            points: 1,
            type: 'multiple-choice'
          },
          {
            id: 'datascience-intro-q2',
            question: 'What are the three main components of data science?',
            options: [
              'Programming, databases, visualization',
              'Statistics, programming, domain expertise',
              'Machine learning, AI, deep learning',
              'Data collection, storage, analysis'
            ],
            correctAnswer: 1,
            difficulty: 'medium',
            explanation: 'The three pillars of data science are statistics/mathematics, programming/technology skills, and domain expertise for understanding business problems.',
            points: 2,
            type: 'multiple-choice'
          },
          {
            id: 'datascience-intro-q3',
            question: 'What is the first step in the data science process?',
            options: [
              'Data collection',
              'Model building',
              'Problem definition',
              'Data visualization'
            ],
            correctAnswer: 2,
            difficulty: 'easy',
            explanation: 'Problem definition is the crucial first step where you identify business questions, define success metrics, and understand stakeholder needs.',
            points: 1,
            type: 'multiple-choice'
          },
          {
            id: 'datascience-intro-q4',
            question: 'Which Python library is primarily used for data manipulation and analysis?',
            options: [
              'NumPy',
              'Matplotlib',
              'Pandas',
              'Scikit-learn'
            ],
            correctAnswer: 2,
            difficulty: 'medium',
            explanation: 'Pandas is the primary Python library for data manipulation and analysis, providing data structures and operations for working with structured data.',
            points: 2,
            type: 'multiple-choice'
          },
          {
            id: 'datascience-intro-q5',
            question: 'What does EDA stand for in data science?',
            options: [
              'Enhanced Data Analysis',
              'Exploratory Data Analysis',
              'Extended Database Application',
              'Efficient Data Algorithms'
            ],
            correctAnswer: 1,
            difficulty: 'easy',
            explanation: 'EDA stands for Exploratory Data Analysis, which involves investigating datasets to discover patterns, spot anomalies, and understand data characteristics.',
            points: 1,
            type: 'multiple-choice'
          }
        ],
        passThreshold: 80
      }
    }
  ]
};
